import React from "react";
import { useIntl } from "react-intl";
import ZipCodesAndCities from "../../data/zipCodes.json";
import { GetTranslation } from "../../translations/GetTranslation";
import { CountrySelect } from "./customAutocomplete/CountrySelect";
import GridTextField from "./customGrid/GridTextField";
import { isInvalidZipCode } from "../../utils/ZipCodeUtils";

export interface AddressFieldsValues {
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

export interface AddressFieldsValidation {
  isStreetEmpty: boolean;
  isStreetNumberEmpty: boolean;
  isZipCodeEmpty: boolean;
  isCityEmpty: boolean;
  isCountryEmpty: boolean;
}

type AddressFieldsProps = {
  fieldValues: AddressFieldsValues;
  fieldsValidation?: AddressFieldsValidation;
  handleChange: (change: Partial<AddressFieldsValues>) => void;
};

export function AddressFields({ fieldValues, fieldsValidation, handleChange }: AddressFieldsProps) {
  const intl = useIntl();

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const zipCode = event.target.value;
    const match = ZipCodesAndCities.find((pair) => pair.zipCode === zipCode);
    handleChange({
      zipCode,
      city: match ? match.city : "",
    });
  };

  const handleCountryChange = (value?: string) => handleChange({ country: value });

  return (
    <>
      <GridTextField
        error={fieldsValidation?.isStreetEmpty}
        required
        label={GetTranslation(intl, "person.field.street")}
        xs={12}
        sm={6}
        md={3}
        onChange={(event) => handleChange({ street: event.target.value })}
        value={fieldValues.street || ""}
      />
      <GridTextField
        error={fieldsValidation?.isStreetNumberEmpty}
        required
        label={GetTranslation(intl, "person.field.streetNumber")}
        xs={12}
        sm={6}
        md={2}
        onChange={(event) => handleChange({ streetNumber: event.target.value })}
        value={fieldValues.streetNumber || ""}
        maxLength={9}
      />
      <GridTextField
        error={fieldsValidation?.isZipCodeEmpty || isInvalidZipCode(fieldValues.zipCode, fieldValues.country)}
        required
        label={GetTranslation(intl, "person.field.zipCode")}
        xs={12}
        sm={4}
        md={2}
        onChange={handleZipCodeChange}
        value={fieldValues.zipCode || ""}
      />
      <GridTextField
        error={fieldsValidation?.isCityEmpty}
        required
        label={GetTranslation(intl, "person.field.city")}
        xs={12}
        sm={4}
        md={2}
        onChange={(event) => handleChange({ city: event.target.value })}
        value={fieldValues.city || ""}
      />
      <CountrySelect
        xs={12}
        sm={4}
        md={3}
        label={GetTranslation(intl, "person.field.country")}
        value={fieldValues.country}
        onChange={handleCountryChange}
        required
        error={fieldsValidation?.isCountryEmpty}
      />
    </>
  );
}
